import { useEffect, useRef } from "react";
import IconButton from "@mui/material/IconButton";
import { appState } from "../../../state";
import { AvatarImage } from "../../components/AvatarImage";
import type { ChatMessage } from "../../../types";

// Same real per-message clock time as TogetherChatStream.tsx's clockTime()
// - kept as its own small copy rather than a shared import, since these
// are the only two consumers and it's a one-line computation.
function clockTime(iso: string): string {
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return "";
  return new Date(ms).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

// Opt-in scrollback, not the primary way to chat anymore - TogetherChatInput
// .tsx is always visible and TogetherChatStream.tsx shows new messages as
// they arrive, so most of a session never needs this at all. It exists for
// "what did I miss" after glancing away - read-only (no composer here;
// there's only ever one input, shared via useTogetherChatMessages.ts).
export function TogetherChatPanel({
  open,
  messages,
  partnerAvatarFilename,
  onClose,
}: {
  open: boolean;
  messages: ChatMessage[];
  // Real presence data, threaded from TogetherRoot.tsx - same source
  // TogetherChatStream.tsx already uses, never a placeholder.
  partnerAvatarFilename: string | null;
  onClose: () => void;
}) {
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // el.scrollTop = el.scrollHeight, not el.scrollTo(...) - matches
    // ChatThreadScreen.tsx's own scrollToBottom() exactly, and jsdom (used
    // by this app's real-server/real-bundle test harness) implements the
    // former but not Element.scrollTo, unlike real browsers which have both.
    const el = listRef.current;
    if (open && el) el.scrollTop = el.scrollHeight;
  }, [open, messages]);

  if (!open) return null;

  return (
    // Dark-glass, not the shared .glass class - .glass is tuned for the
    // app's normal light/dark THEME background, but this panel floats over
    // Together Mode's black video stage regardless of theme. In light
    // theme especially, .glass's ~75%-opaque white read as washed-out grey
    // over black (same issue TogetherChatInput.tsx's own comment
    // documents), and its received-message bubbles used color:"inherit" -
    // the app's normal dark ink text color - which is nearly illegible on
    // any surface this translucent over a black backdrop. Same
    // rgba(15,12,14,0.65)+blur+#fff recipe as .together-status-pill/
    // .together-dock/.pause-ask-card, so every Together Mode surface is
    // now visually consistent.
    <div
      className="together-chat-panel d-flex flex-column"
      style={{
        position: "fixed",
        // Above both the reaction toolbar AND the composer, both docked
        // lower - on a narrow phone a 280px-wide panel anchored right and
        // a 6-icon reaction row anchored left at the same height would
        // otherwise overlap in the middle.
        bottom: "calc(136px + env(safe-area-inset-bottom, 0px))",
        right: 16,
        width: 280,
        maxWidth: "calc(100vw - 32px)",
        height: "min(320px, 45vh)",
        zIndex: 1400,
      }}
    >
      <div className="together-chat-panel-header d-flex align-items-center justify-content-between px-3 py-2">
        <span className="small fw-semibold">
          <i className="bi bi-chat-dots-fill me-1 text-pink" aria-hidden="true" />
          Chat history
        </span>
        <IconButton aria-label="Close chat history" size="small" onClick={onClose} sx={{ color: "inherit" }}>
          <i className="bi bi-x-lg" aria-hidden="true" />
        </IconButton>
      </div>

      <div ref={listRef} className="flex-grow-1 overflow-auto px-2 py-2" style={{ minHeight: 0 }}>
        {!messages.length && <p className="small text-center mb-0 mt-3 together-chat-panel-empty">No messages yet.</p>}
        {messages.map((m) => {
          const isMine = m.senderEmail === appState.currentUserEmail;
          const avatarFilename = isMine ? appState.currentUserAvatarFilename : partnerAvatarFilename;
          const initial = (m.senderName || "?").trim().charAt(0).toUpperCase();
          return (
            <div key={m.id} className="together-chat-panel-row">
              <span className="together-chat-panel-avatar" aria-hidden="true">
                {avatarFilename ? (
                  <AvatarImage
                    filename={avatarFilename}
                    alt=""
                    style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }}
                    fallback={initial}
                  />
                ) : (
                  initial
                )}
              </span>
              <div className="together-chat-panel-col">
                <div className="together-chat-panel-meta">
                  <span className={`together-chat-panel-name${isMine ? " is-mine" : ""}`}>{isMine ? "You" : m.senderName}</span>
                  <span className="together-chat-panel-time">{clockTime(m.createdAt)}</span>
                </div>
                <div className="together-chat-panel-bubble">{m.text}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
